import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listExamConfigs, listExamResults, listExamMarksForClass } from "../../api/examApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");

/**
 * Inline equivalent of digicampusbd's `exam-general-tabulation-sheet` /
 * `exam-general-tabulation-sheet-download` ("Details Tabulation Sheet") —
 * every student in a class × every subject, one grid, matching the old
 * page's own shape (`GeneralTabulationSheetResponse`: one row per student,
 * one column-group per subject). The old page's real download is a Jasper
 * PDF (`/jasper/general/tabulation/sheet/download`) — per this project's
 * established pattern (Testimonial/TC/Certification, Phase 12), that isn't
 * ported; this shows the same underlying data as a browser-printable HTML
 * table instead of generating a PDF file, reusing `GET /api/exam-results`
 * and `GET /api/exam-marks/for-class` (both already built) rather than any
 * new backend endpoint.
 *
 * One documented simplification: the old response breaks each subject
 * further into its own short-code columns (written/MCQ/practical, up to 6
 * per subject) — this shows each subject's already-aggregated total instead
 * of re-deriving that same short-code breakdown a second time in a second
 * place; the mark-entry grid (`exam-mark-input`) is where those individual
 * short codes are actually entered and reviewed.
 *
 * Also serves 3 more urls confirmed to hit the *exact same* old-app backend
 * endpoints, not just a similar-sounding name — read each template's own
 * download-button handler directly: `exam-average-tabulation-sheet` ("Short
 * Code Wise Tabulation") and `exam-bottom-tabulation-sheet` ("Details
 * Tabulation Sheet 3") both call `/jasper/general/tabulationsheet/response/two`,
 * the identical endpoint `exam-general-tabulation-sheet` itself calls;
 * `exam-general-short-tabulation-sheet-download` ("Total Mark Wise
 * Tabulation") calls the sibling `/response` (no "/two") endpoint — a
 * distinct old-app service method, but one whose own real difference from
 * `/response/two` (per `TabulationService`) is exactly the short-code-vs-
 * aggregated-total distinction this page already simplifies away above, so
 * the same page correctly serves it too rather than needing a second,
 * near-duplicate one.
 */
export default function ExamTabulationSheetPage() {
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");

  const [results, setResults] = useState([]);
  const [marks, setMarks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
    });
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);
    if (!cc) return;
    listExamConfigs(cc.classId).then((configs) => {
      setExamConfigs(configs);
      setExamConfigId(configs.length > 0 ? String(configs[0].examConfigId) : "");
    });
  }, [classConfigurationId, classConfigs]);

  useEffect(() => {
    if (!examConfigId || !classConfigurationId || !academicYear) {
      setResults([]);
      setMarks([]);
      return;
    }
    setLoading(true);
    setError("");
    let cancelled = false;
    Promise.all([listExamResults(examConfigId), listExamMarksForClass(examConfigId, classConfigurationId, academicYear)])
      .then(([r, m]) => {
        if (!cancelled) {
          setResults(r.filter((row) => String(row.classConfigurationId) === classConfigurationId && row.academicYear === academicYear));
          setMarks(m);
        }
      })
      .catch(() => setError("Failed to load tabulation data."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [examConfigId, classConfigurationId, academicYear]);

  const subjectNames = useMemo(() => {
    const seen = new Set();
    marks.forEach((m) => seen.add(m.subjectName));
    return Array.from(seen).sort();
  }, [marks]);

  const marksByStudentSubject = useMemo(() => {
    const map = new Map();
    marks.forEach((m) => {
      map.set(`${m.identificationId}::${m.subjectName}`, m);
    });
    return map;
  }, [marks]);

  const rows = results
    .slice()
    .sort((a, b) => a.studentRoll - b.studentRoll)
    .map((r) => ({
      ...r,
      subjectScores: subjectNames.map((name) => marksByStudentSubject.get(`${r.identificationId}::${name}`)),
    }));

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Details Tabulation Sheet</h1>
      </div>

      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      <div className="attendance-filters">
        <label>
          Class
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No exams configured</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examConfigId} value={ec.examConfigId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-secondary" onClick={() => window.print()} disabled={rows.length === 0}>
          Print
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="table-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Roll</th>
                <th>Student ID</th>
                <th>Name</th>
                {subjectNames.map((name) => (
                  <th key={name}>{name}</th>
                ))}
                <th>Total</th>
                <th>Grade</th>
                <th>GPA</th>
                <th>Position</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.resultId}>
                  <td>{r.studentRoll}</td>
                  <td>{r.customStudentId}</td>
                  <td>{r.studentName}</td>
                  {r.subjectScores.map((mark, i) => (
                    <td key={subjectNames[i]}>{mark ? mark.totalScore : "—"}</td>
                  ))}
                  <td>{r.obtainMark}</td>
                  <td>{r.letterGrade}</td>
                  <td>{r.avgGpaWithoutOptional}</td>
                  <td>{r.classPosition === 0 ? "—" : r.classPosition}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && rows.length === 0 && <p>No results found for this selection.</p>}
    </MainLayout>
  );
}
